<template>
	<div class="chart-finance-summary-box">
		<chart-boxes-container :data="chartsData" :tabs="true" extra-class="single" @changeTab="handleChangeTab"/>
		<div class="content-tabs-box">
			<div class="content-tabs-item" v-if="activeTab === 0">
				<finance-summary-box :properties="properties" :debts="debts" :payments="payments" :payment-frequency="paymentFrequency"/>
			</div>
			<div class="content-tabs-item" v-if="activeTab === 1">
				<div class="finance-panel-group">
					<div class="actions">
						<button class="button style-3" @click.stop.prevnet="$store.commit('SHOW_PROPERTY_CREATE_FORM')">
							<span class="inner">{{ $t('property.add_property') }}</span>
						</button>
					</div>
					<h3 class="finance-panel-group-title">{{ $t('property.property_list') }}</h3>
					<finance-panel-boxes-container :data="propertiesPanelData" v-if="propertiesPanelData.length"/>
					<p v-else>{{ $t('home.no_properties') }}</p>
				</div>
			</div>
			<div class="content-tabs-item" v-if="activeTab === 2">
				<div class="finance-panel-group">
					<div class="actions">
						<button class="button style-3" @click.stop.prevnet="$store.commit('SHOW_DEBT_CREATE_FORM')">
							<span class="inner">{{ $t('debts.add_debt') }}</span>
						</button>
					</div>
					<h3 class="finance-panel-group-title">{{ $t('debts.debts_list') }}</h3>
					<finance-panel-boxes-container :data="debtsPanelsData" v-if="debtsPanelsData.length"/>
					<p v-else>{{ $t('home.no_debts') }}</p>
				</div>
			</div>
			<div class="content-tabs-item" v-if="activeTab === 3">
				<div class="finance-panel-group">
					<div class="actions">
						<button class="button style-3" @click.stop.prevnet="$store.commit('SHOW_PAYMENT_CREATE_FORM')">
							<span class="inner">{{ $t('payments.add_payment') }}</span>
						</button>
					</div>
					<h3 class="finance-panel-group-title">{{ $t('payments.payments_list') }}</h3>
					<finance-panel-boxes-container :data="paymentsPanelsData" v-if="paymentsPanelsData.length"/>
					<p v-else>{{ $t('home.no_payments') }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ChartBoxesContainer from "@/components/containers/ChartBoxesContainer";
import pageFunctionsMixin from "@/mixins/pageFunctionsMixin";
import investmentsMixin from "@/mixins/investmentsMixin";
import FinanceSummaryBox from "@/components/containers/FinanceSummaryBox.vue";
import FinancePanelBoxesContainer from "@/components/containers/FinancePanelBoxesContainer.vue";

export default {
	name: "ChartFinanceSummary",
	props: ['debts', 'properties', 'payments'],
	mixins: [pageFunctionsMixin, investmentsMixin],
	components: {
		FinancePanelBoxesContainer,
		ChartBoxesContainer,
		FinanceSummaryBox
	},
	data() {
		return {
			paymentFrequency: 'monthly',
			activeTab: 0
		}
	},
	computed:{
		paymentsPanelsData(){
			let data = [];

			this.$store.state.views.payments.forEach((item) => {
				data.push({
					id: item.id,
					icon: item.icon,
					name: item.name,
					value: this.paymentFrequency === 'monthly' ? item.monthly_amount : item.yearly_amount,
					type: item.type,
					finance_type: 'payment'
				});
			});

			return data;
		},
		debtsPanelsData(){
			let data = [];
			this.$store.state.views.debts.forEach((item) => {
				data.push({
					id: item.id,
					icon: item.icon,
					name: item.name,
					value: item.amount || 0,
					type: item.type,
					finance_type: 'debt'
				});
			});

			return data;
		},
		propertiesPanelData(){
			let data = [];
			this.$store.state.views.properties.forEach((item) => {
				let icon = this.getPropertyIcon(item.type);
				data.push({
					id: item.id,
					icon,
					name: item.name,
					value: item.value,
					finance_type: 'property',
					property_type: item.property_type,
					investment_account_type: item.investment_account_type,
				});
			});

			return data;
		},
		totalProperties(){
			let value = 0;
			this.properties.forEach(item => {
				if(item.value){
					value += parseFloat(item.value);
				}
			});

			return value;
		},
		totalDebts(){
			let value = 0;
			this.debts.forEach(item => {
				if(item.amount){
					value += parseFloat(item.amount);
				}
			});

			return value;
		},
		chartsData(){
			let data = [
				this.summaryChart,
				this.propertiesChart,
				this.debtsChart,
				this.paymentsChart,
			];

			return data;
		},
		summaryChart(){
			let data = this.getPortfoliosChartOptions();
			data.tabName = this.$t('home.net_worth');
			data.showInvestmentUpdate = true;

			let series = [
				parseFloat(this.totalProperties.toFixed(2)),
				parseFloat(this.totalDebts.toFixed(2)),
			];
			let labels = [
				this.$t('routes.properties.title'),
				this.$t('routes.debts.title')
			];

			data.chartOptions.labels = labels;
			data.series = series;

			return data;
		},
		propertiesChart(){
			let data = this.getPortfoliosChartOptions();
			data.tabName = this.$t('routes.properties.title');

			let types = {
				car: 0,
				real_estate: 0,
				land: 0,
				valuable: 0,
				intangible_asset: 0,
				investment: 0,
				other: 0,
			};

			this.properties.forEach((item) => {
				let value = parseFloat(item.value);
				types[item.type] += value;
			});

			let labels = [];
			let series = [];

			Object.entries(types).forEach(([key, value]) => {
				if(value > 0){
					labels.push(this.$t(`property.${key}_graph`));
					series.push(Math.round(value * 100) / 100);
				}
			});

			data.chartOptions.labels = labels;
			data.series = series;

			return data;
		},
		debtsChart(){
			let data = this.getPortfoliosChartOptions();
			data.tabName = this.$t('routes.debts.title');

			let categories = {};

			this.debts.forEach((item) => {
				let value = parseFloat(item.amount);
				if(!categories[item.category]) {
					categories[item.category] = 0;
				}

				categories[item.category] += value;
			});

			let labels = [];
			let series = [];

			Object.entries(categories).forEach(([key, value]) => {
				if(value > 0){
					labels.push(key);

					let tmpValue = parseFloat(value.toFixed(2));
					series.push(tmpValue);
				}
			});

			data.chartOptions.labels = labels;
			data.series = series;

			return data;
		},

		paymentsChart(){
			let data = this.getPortfoliosChartOptions();
			data.tabName = this.$t('routes.payments.title');
			let groups = {};

			this.$store.state.views.payments.forEach((item) => {
				let monthlyValue = parseFloat(item.monthly_amount);
				let yearlyValue = parseFloat(item.yearly_amount);
				if(!groups[item.group]) {
					groups[item.group] = {
						monthly: 0,
						yearly: 0,
					};
				}

				groups[item.group].monthly += monthlyValue;
				groups[item.group].yearly += yearlyValue;
			});

			let labels = [];
			let monthlySeries = [];
			let yearlySeries = [];

			Object.entries(groups).forEach(([key, value]) => {
				labels.push(key);
				monthlySeries.push(value.monthly);
				yearlySeries.push(value.yearly);
			});

			data.toggleSeries = true;
			data.seriesLabel_1 = this.$t('forms.monthly');
			data.seriesLabel_2 = this.$t('forms.yearly');
			data.chartOptions.labels = labels;

			if(this.paymentFrequency === 'monthly'){
				data.series = monthlySeries;
			} else {
				data.series = yearlySeries;
			}

			return data;
		},
	},
	methods: {
		getPropertyIcon(type) {
			let icon;
			switch (type) {
				case 'car':
					icon = 'fa-car';
					break;
				case 'real_estate':
					icon = 'fa-house';
					break;
				case 'land':
					icon = 'fa-map';
					break;
				case 'valuable':
					icon = 'fa-gem';
					break;
				case 'intangible_asset':
					icon = 'fa-brain';
					break;
				case 'investment':
					icon = 'fa-chart-line-up';
					break;
				default:
					icon = 'fa-wallet';
					break;
			}

			return `<i class="fa-thin ${icon}"></i>`;
		},
		handleChangeTab(data){
			this.activeTab = data;
		},
		processSeriesSwitcherChange(data){
			this.paymentFrequency = data.value ? 'yearly' : 'monthly';
		}
	},
	mounted() {
		this.$root.$on('series-switcher-change', this.processSeriesSwitcherChange)
	},
	destroyed() {
		this.$root.$off('series-switcher-change', this.processSeriesSwitcherChange);
	}
}
</script>

<style scoped lang="scss">
.finance-panel-group-title {
	font-size: 20px;
}

.finance-panel-group {
	.actions {
		display: flex;
		justify-content: flex-end;
		margin-bottom: 25px;
		margin-top: -25px;
	}
	@media($tabletL){
		.actions {
			margin-top: 0;
		}
	}
	@media($mobileM){
		.actions {
			.button {
				width: 100%;
			}
		}
	}
}

.chart-finance-summary-box {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 15px;
	width: 100%;

	&:last-child {
		margin-bottom: 0;
	}

	.chart-boxes-container {
		width: 45%;
	}

	.content-tabs-box {
		width: calc(55% - 30px);
	}

	@media($desktopM){
		.chart-boxes-container {
			width: 50%;
		}

		.content-tabs-box {
			width: calc(50% - 15px);
		}
	}

	@media($tabletL) {
		flex-direction: column;
		.chart-boxes-container {
			width: 100%;
			margin-bottom: 30px;
			max-width: 500px;
		}
		.content-tabs-box {
			width: 100%;
			margin-top: 0;
		}
	}
	@media($mobileM){
		.chart-boxes-container {
			margin-bottom: 15px;
		}
	}
}
</style>